var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { busService } from "../bus";
import { Box3, Sphere } from "flavr-math";
import { MulticastDelegate1 } from "flavr-common";
export const onRoomBoundariesLoadedDelegate = new MulticastDelegate1();
class MeshBoundingPromise {
    constructor() {
        this.resolve = () => { };
        this.reject = () => { };
        this.isLoaded = false;
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
        this.promise.then((bounding) => {
            this.bounding = bounding;
            this.isLoaded = true;
        }).catch(e => { console.error(e); });
    }
    wait() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.promise;
        });
    }
}
class MeshBoundingService {
    constructor() {
        this.connection = busService.connection("MeshBoundingService");
        this.meshData = new Map();
        this.pendingRoomsByMeshUrl = new Map();
        this.numWaitingBoundsPerRoom = new Map();
        this.numWaitingBounds = 0;
    }
    /// Getters ///
    hasBounding(meshUrl) {
        return this.meshData.has(meshUrl);
    }
    getBounding(meshUrl, roomUid) {
        return __awaiter(this, void 0, void 0, function* () {
            const boundingPromise = this.getOrCreatePromise(meshUrl, roomUid);
            return boundingPromise.bounding;
        });
    }
    getBoundingOnceFinished(meshUrl, roomUid) {
        return __awaiter(this, void 0, void 0, function* () {
            const boundingPromise = this.getOrCreatePromise(meshUrl, roomUid);
            if (boundingPromise.isLoaded)
                return boundingPromise.bounding;
            return yield boundingPromise.wait();
        });
    }
    getBoundingDataPromise(meshUrl, roomUid) {
        return this.getOrCreatePromise(meshUrl, roomUid);
    }
    /// Bounds helper methods ///
    generateFullMeshUrl(meshName, pieceName) {
        return (pieceName === undefined) ? meshName : `${meshName}@${pieceName}`;
    }
    updateBounding(meshUrl, boundingBox, boundingSphere) {
        let meshBoundingPromise = this.meshData.get(meshUrl);
        if (meshBoundingPromise === undefined) {
            meshBoundingPromise = new MeshBoundingPromise();
            this.meshData.set(meshUrl, meshBoundingPromise);
        }
        const bounding = {
            boundingBox: new Box3().copy(boundingBox),
            boundingSphere: new Sphere().copy(boundingSphere)
        };
        meshBoundingPromise.resolve(bounding);
        this.releaseRoomDependencies(meshUrl);
        if (this.numWaitingBounds > 0) {
            this.numWaitingBounds--;
            if (this.numWaitingBounds === 0) {
                console.debug("[mesh] All boundaries were loaded");
            }
        }
    }
    /// Private promise helper methods ///
    getOrCreatePromise(meshUrl, roomUid) {
        let deferred = this.meshData.get(meshUrl);
        if (deferred === undefined) {
            deferred = new MeshBoundingPromise();
            this.meshData.set(meshUrl, deferred);
            this.numWaitingBounds++;
        }
        if (!deferred.isLoaded && roomUid !== undefined) {
            this.trackRoomDependency(meshUrl, roomUid);
        }
        return deferred;
    }
    trackRoomDependency(meshUrl, roomUid) {
        var _a;
        let roomSet = this.pendingRoomsByMeshUrl.get(meshUrl);
        if (roomSet === undefined) {
            roomSet = new Set();
            this.pendingRoomsByMeshUrl.set(meshUrl, roomSet);
        }
        if (roomSet.has(roomUid))
            return;
        roomSet.add(roomUid);
        this.numWaitingBoundsPerRoom.set(roomUid, ((_a = this.numWaitingBoundsPerRoom.get(roomUid)) !== null && _a !== void 0 ? _a : 0) + 1);
    }
    releaseRoomDependencies(meshUrl) {
        const roomSet = this.pendingRoomsByMeshUrl.get(meshUrl);
        if (roomSet === undefined)
            return;
        this.pendingRoomsByMeshUrl.delete(meshUrl);
        for (const roomUid of roomSet) {
            const currentPending = this.numWaitingBoundsPerRoom.get(roomUid);
            if (currentPending === undefined || currentPending <= 0)
                continue;
            const nextPending = currentPending - 1;
            if (nextPending <= 0) {
                this.numWaitingBoundsPerRoom.delete(roomUid);
                onRoomBoundariesLoadedDelegate.invoke(roomUid);
            }
            else {
                this.numWaitingBoundsPerRoom.set(roomUid, nextPending);
            }
        }
    }
}
export const meshBoundingService = new MeshBoundingService();
